import React, { useState, useEffect } from 'react';
import { 
  Container, Card, Image, Spinner, Button, Badge 
} from 'react-bootstrap';
import { BASE_URL, UPLOAD_BASE_URL } from '../../services/api';

const LeadershipMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchLeadershipMessages();
  }, []);

  const fetchLeadershipMessages = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${BASE_URL}/leadership_messages.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setMessages(result.data);
      } else {
        setError(result.message || 'Failed to fetch leadership messages');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Error loading leadership messages: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    
    if (photoPath.startsWith('http')) return photoPath;
    
    if (photoPath.startsWith('uploads/')) {
      return `${UPLOAD_BASE_URL}/${photoPath}`;
    }
    
    return `${UPLOAD_BASE_URL}/${photoPath}`;
  };

  const getDefaultPhoto = (designation) => {
    const iconMap = {
      'Chairman': 'fas fa-crown',
      'President': 'fas fa-flag',
      'Principal': 'fas fa-user-graduate',
      'Vice Principal': 'fas fa-user-graduate',
      'Director': 'fas fa-user-tie',
      'Dean': 'fas fa-user-md',
      'Head of Department': 'fas fa-user-cog',
      'Manager': 'fas fa-briefcase',
      'Coordinator': 'fas fa-tasks',
      'Supervisor': 'fas fa-eye',
      'Secretary': 'fas fa-file-alt',
      'Treasurer': 'fas fa-money-bill-wave',
      'Board Member': 'fas fa-users',
      'Trustee': 'fas fa-shield-alt',
      'Founder': 'fas fa-lightbulb',
      'CEO': 'fas fa-chart-line',
      'Teacher': 'fas fa-chalkboard-teacher',
      'Mentor': 'fas fa-hands-helping',
      'default': 'fas fa-user-circle'
    };
    
    for (const [key, icon] of Object.entries(iconMap)) {
      if (designation?.includes(key)) return icon;
    }
    return iconMap.default;
  };

  const getDesignationColor = (designation) => {
    const colorMap = {
      'Chairman': 'warning',
      'President': 'danger',
      'Principal': 'success',
      'Vice Principal': 'primary',
      'Director': 'info',
      'Dean': 'dark',
      'Head of Department': 'secondary',
      'default': 'secondary'
    };
    
    for (const [key, color] of Object.entries(colorMap)) {
      if (designation?.includes(key)) return color;
    }
    return colorMap.default;
  };

  const getDesignationGroups = () => {
    const groups = {
      all: {
        name: 'All Leaders',
        icon: 'fas fa-users',
        color: 'primary',
        messages: messages
      }
    };

    messages.forEach(message => {
      if (!groups[message.designation]) {
        groups[message.designation] = {
          name: message.designation,
          icon: getDefaultPhoto(message.designation),
          color: getDesignationColor(message.designation),
          messages: []
        };
      }
      groups[message.designation].messages.push(message);
    });

    return groups;
  };

  const getActiveMessages = () => {
    const groups = getDesignationGroups();
    return groups[activeTab]?.messages || [];
  };

  const handleTabSelect = (tabKey) => {
    setActiveTab(tabKey);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return '';
    }
  };

  const getDesignationGroupsArray = () => {
    const groups = getDesignationGroups();
    return Object.entries(groups)
      .filter(([key]) => key !== 'all')
      .sort((a, b) => b[1].messages.length - a[1].messages.length);
  };

  // Enhanced function to split message into paragraphs
  const splitIntoParagraphs = (content) => {
    if (!content) return [];
    
    let cleanedContent = content.trim();
    
    // Handle asterisk-based formatting
    if (cleanedContent.includes('*') && cleanedContent.split('*').length > 2) {
      // Remove the leading asterisk and split by asterisk
      const paragraphs = cleanedContent
        .replace(/^\*\s*/, '') // Remove leading asterisk
        .split(/\*\s*/)
        .filter(p => p.trim() !== '')
        .map(p => p.trim());
      
      if (paragraphs.length > 0) {
        return paragraphs;
      }
    }
    
    // Handle numbered or bulleted lists
    if (cleanedContent.match(/^\d+\.\s+|^[•\-]\s+/m)) {
      const paragraphs = cleanedContent
        .split(/\n\s*\d+\.\s+|\n\s*[•\-]\s+/)
        .filter(p => p.trim() !== '')
        .map(p => p.trim());
      
      if (paragraphs.length > 0) {
        return paragraphs;
      }
    }
    
    // Handle double newlines (most common paragraph separation)
    const doubleNewlineParagraphs = cleanedContent
      .split(/\n\s*\n+/)
      .filter(p => p.trim() !== '')
      .map(p => p.trim());
    
    if (doubleNewlineParagraphs.length > 1) {
      return doubleNewlineParagraphs;
    }
    
    // Return as single paragraph
    return [cleanedContent];
  };

  const renderEmptyState = () => (
    <div className="empty-state-container">
      <div className="empty-state-animation">
        <div className="empty-state-icon">
          <i className="fas fa-users-slash"></i>
        </div>
      </div>
      <h3 className="empty-state-title">No Leadership Messages</h3>
      <p className="empty-state-description">
        There are no leadership messages available for this category at the moment.
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="fullpage-loader">
        <div className="loader-container">
          <div className="loader-spinner">
            <div className="spinner-circle"></div>
            <div className="spinner-circle"></div>
            <div className="spinner-circle"></div>
          </div>
          <h3 className="loader-text">Loading Leadership Portal</h3>
          <p className="loader-subtext">Gathering wisdom from our leaders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fullpage-error">
        <div className="error-container">
          <div className="error-icon">
            <i className="fas fa-exclamation-triangle"></i>
          </div>
          <h3 className="error-title">Connection Error</h3>
          <p className="error-description">{error}</p>
          <Button 
            variant="primary" 
            onClick={fetchLeadershipMessages}
            className="error-retry-btn"
          >
            <i className="fas fa-redo me-2"></i>
            Retry Connection
          </Button>
        </div>
      </div>
    );
  }

  const designationGroups = getDesignationGroupsArray();
  const activeMessages = getActiveMessages();

  return (
    <div className="leadership-portal-container">
      {/* Main Content */}
      <main className="portal-content">
        <Container fluid className="main-container">
          {/* Title Section */}
          <div className="title-section">
            <div className="title-container">
              <h1 className="main-title">Leadership Messages</h1>
              <p className="title-subtext">Insights and visions from our esteemed leaders</p>
              <div className="title-decoration">
                <div className="decoration-line"></div>
                <div className="decoration-dot"></div>
                <div className="decoration-line"></div>
              </div>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="tabs-section">
            <div className="tabs-container">
              <div className="tabs-scroll">
                <div className="tabs-wrapper">
                  <button
                    className={`tab-button ${activeTab === 'all' ? 'active' : ''}`}
                    onClick={() => handleTabSelect('all')}
                  >
                    <i className="fas fa-users me-2"></i>
                    All Leaders
                    <Badge bg="primary" className="tab-badge">
                      {messages.length}
                    </Badge>
                  </button>
                  
                  {designationGroups.map(([key, group]) => (
                    <button
                      key={key}
                      className={`tab-button ${activeTab === key ? 'active' : ''}`}
                      onClick={() => handleTabSelect(key)}
                    >
                      <i className={`${group.icon} me-2`}></i>
                      {group.name}
                      <Badge bg={group.color} className="tab-badge">
                        {group.messages.length}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* Messages Container */}
          <div className="messages-section">
            {activeMessages.length === 0 ? (
              renderEmptyState()
            ) : (
              <div className="messages-container">
                {activeMessages.map((message, index) => {
                  const paragraphs = splitIntoParagraphs(message.message_content);
                  
                  return (
                    <Card 
                      key={message.id || index} 
                      className="leader-message-card"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <Card.Body>
                        <div className="leader-content-wrapper">
                          {/* Left Side - Large Photo and Basic Info */}
                          <div className="leader-photo-side">
                            <div className="leader-photo-container">
                              <div className="leader-photo-wrapper">
                                {message.profile_photo ? (
                                  <Image
                                    src={getPhotoUrl(message.profile_photo)}
                                    alt={message.leader_name}
                                    fluid
                                    className="leader-photo-large"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.querySelector('.photo-icon-large').style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div className="photo-icon-large" style={message.profile_photo ? { display: 'none' } : {}}>
                                  <i className={getDefaultPhoto(message.designation)}></i>
                                </div>
                                {message.is_active && (
                                  <span className="active-indicator-large" title="Active Leader">
                                    <i className="fas fa-circle"></i>
                                  </span>
                                )}
                                {message.show_on_homepage && (
                                  <span className="featured-badge-large" title="Featured on Homepage">
                                    <i className="fas fa-star"></i>
                                  </span>
                                )}
                              </div>
                              
                              <div className="leader-basic-info-side">
                                <h3 className="leader-name-side">
                                  {message.leader_name}
                                </h3>
                                <Badge bg={getDesignationColor(message.designation)} className="role-badge-side">
                                  <i className={`${getDefaultPhoto(message.designation)} me-1`}></i>
                                  {message.designation}
                                </Badge>
                                
                                {message.created_date && (
                                  <div className="message-date-side">
                                    <i className="fas fa-calendar-alt me-1"></i>
                                    <span>{formatDate(message.created_date)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Right Side - Message Content */}
                          <div className="message-content-side">
                            {/* Message Title */}
                            {message.message_title && (
                              <div className="message-title-side">
                                <h4 className="message-title-text">
                                  <i className="fas fa-quote-left quote-icon-start-side"></i>
                                  {message.message_title}
                                  <i className="fas fa-quote-right quote-icon-end-side"></i>
                                </h4>
                                <div className="title-underline-side"></div>
                              </div>
                            )}
                            
                            {/* Message Content */}
                            <div className="message-text-side">
                              {paragraphs.length > 0 ? (
                                <div className="paragraphs-container-side">
                                  {paragraphs.map((paragraph, pIndex) => (
                                    <div 
                                      key={pIndex} 
                                      className="paragraph-wrapper-side"
                                    >
                                      <p 
                                        className="paragraph-item-side"
                                        style={{ 
                                          animationDelay: `${pIndex * 0.15}s`
                                        }}
                                      >
                                        {paragraph}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="single-paragraph-side">
                                  <p className="full-message-side">
                                    <i className="fas fa-quote-left quote-icon-inline-start-side"></i>
                                    {message.message_content}
                                    <i className="fas fa-quote-right quote-icon-inline-end-side"></i>
                                  </p>
                                </div>
                              )}
                              
                              {/* Closing Quote */}
                              {message.closing_quote && (
                                <div className="closing-quote-side">
                                  <div className="closing-quote-wrapper-side">
                                    <i className="fas fa-hand-peace closing-icon-side"></i>
                                    <blockquote className="closing-quote-text">
                                      "{message.closing_quote}"
                                    </blockquote>
                                  </div>
                                </div>
                              )}
                              
                              {/* Signature */}
                              <div className="signature-section-side">
                                <div className="signature-line-side"></div>
                                <div className="signature-content-side">
                                  <div className="signature-name-side">
                                    <i className="fas fa-signature me-2"></i>
                                    {message.leader_name}
                                  </div>
                                  <div className="signature-designation-side">
                                    {message.designation}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Message Actions */}
                              <div className="message-actions-side">
                                <button className="action-btn share-btn-side" title="Share this message">
                                  <i className="fas fa-share-alt me-2"></i>
                                  Share
                                </button>
                                <button className="action-btn save-btn-side" title="Save for later">
                                  <i className="fas fa-bookmark me-2"></i>
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </Container>
      </main>

      {/* Custom CSS */}
      <style jsx="true">{`
        /* Main Layout */
        .leadership-portal-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        /* Main Content Layout */
        .portal-content {
          padding: 2rem 0;
        }

        .main-container {
          padding: 0 1.5rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        /* Title Section */
        .title-section {
          text-align: center;
          margin-bottom: 3rem;
          animation: fadeInDown 0.8s ease-out;
        }

        .title-container {
          position: relative;
          padding: 2rem 0;
        }

        .title-icon {
          font-size: 4rem;
          color: #3b82f6;
          margin-bottom: 1.5rem;
          animation: float 3s ease-in-out infinite;
        }

        .main-title {
          font-size: 3.2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.8rem;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: gradientShift 3s ease infinite;
        }

        .title-subtext {
          font-size: 1.2rem;
          color: #64748b;
          max-width: 600px;
          margin: 0 auto 1.5rem;
          line-height: 1.6;
        }

        .title-decoration {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          max-width: 400px;
          margin: 0 auto;
        }

        .decoration-line {
          height: 2px;
          flex: 1;
          background: linear-gradient(90deg, transparent, #cbd5e1, transparent);
        }

        .decoration-dot {
          width: 8px;
          height: 8px;
          background: #3b82f6;
          border-radius: 50%;
          animation: pulseDot 2s infinite;
        }

        /* Tabs Section */
        .tabs-section {
          margin-bottom: 2.5rem;
        }

        .tabs-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          padding: 1rem;
          border: 1px solid #e2e8f0;
        }

        .tabs-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }

        .tabs-scroll::-webkit-scrollbar {
          height: 6px;
        }

        .tabs-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .tabs-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }

        .tabs-wrapper {
          display: flex;
          gap: 1rem;
          padding: 0.5rem 0;
          min-width: max-content;
          justify-content: center;
        }

        .tab-button {
          display: flex;
          align-items: center;
          padding: 1rem 1.8rem;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          color: #475569;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
          position: relative;
          overflow: hidden;
        }

        .tab-button:hover {
          transform: translateY(-2px);
          border-color: #3b82f6;
          box-shadow: 0 5px 15px rgba(59, 130, 246, 0.1);
        }

        .tab-button.active {
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          color: white;
          border-color: transparent;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.25);
        }

        .tab-button.active::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 3px;
          background: white;
          border-radius: 2px;
          animation: slideIn 0.3s ease;
        }

        .tab-badge {
          margin-left: 0.75rem;
          font-size: 0.8rem;
          padding: 0.25rem 0.6rem;
          border-radius: 20px;
          font-weight: 700;
        }

        .tab-button.active .tab-badge {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        /* Messages Section */
        .messages-section {
          margin-top: 1rem;
        }

        .messages-container {
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
        }

        /* Leader Message Cards */
        .leader-message-card {
          border: none;
          border-radius: 20px;
          background: white;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
          animation: cardSlideIn 0.6s ease-out;
          animation-fill-mode: both;
          border: 1px solid #e2e8f0;
        }

        .leader-message-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
        }

        .leader-message-card .card-body {
          padding: 3rem;
        }

        /* Leader Content Wrapper - Left Photo, Right Content */
        .leader-content-wrapper {
          display: grid;
          grid-template-columns: 350px 1fr;
          gap: 3rem;
          align-items: start;
        }

        /* Left Side - Photo Section */
        .leader-photo-side {
          position: relative;
        }

        .leader-photo-container {
          position: sticky;
          top: 2rem;
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .leader-photo-wrapper {
          position: relative;
          width: 100%;
          height: 400px;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          transition: all 0.4s ease;
        }

        .leader-photo-wrapper:hover {
          transform: translateY(-5px) scale(1.02);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
        }

        .leader-photo-large {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: all 0.4s ease;
        }

        .leader-photo-wrapper:hover .leader-photo-large {
          transform: scale(1.05);
        }

        .photo-icon-large {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #6366f1, #4f46e5);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 5rem;
          transition: all 0.4s ease;
        }

        .leader-photo-wrapper:hover .photo-icon-large {
          transform: scale(1.05);
        }

        .active-indicator-large {
          position: absolute;
          bottom: 20px;
          right: 20px;
          width: 24px;
          height: 24px;
          background: #10b981;
          border-radius: 50%;
          border: 4px solid white;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
          animation: pulseIndicatorLarge 2s infinite;
          z-index: 2;
        }

        .featured-badge-large {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 40px;
          height: 40px;
          background: linear-gradient(45deg, #f59e0b, #d97706);
          border-radius: 50%;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          border: 4px solid white;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
          animation: starPulseLarge 2s infinite;
          z-index: 2;
        }

        .leader-basic-info-side {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1.5rem;
          background: white;
          border-radius: 16px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
          border: 1px solid #f1f5f9;
        }

        .leader-name-side {
          font-size: 1.8rem;
          font-weight: 800;
          color: #1e293b;
          margin: 0;
          line-height: 1.2;
          text-align: center;
        }

        .role-badge-side {
          font-size: 1rem;
          padding: 0.8rem 1.5rem;
          border-radius: 50px;
          font-weight: 700;
          text-align: center;
          align-self: center;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          border: 2px solid transparent;
          transition: all 0.3s ease;
        }

        .role-badge-side:hover {
          transform: scale(1.05);
        }

        .message-date-side {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.8rem;
          background: #f8fafc;
          border-radius: 12px;
          color: #64748b;
          font-size: 0.95rem;
          border: 1px solid #e2e8f0;
        }

        .message-date-side i {
          color: #3b82f6;
        }

        /* Right Side - Content Section */
        .message-content-side {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .message-title-side {
          text-align: center;
          margin-bottom: 1rem;
          position: relative;
        }

        .message-title-text {
          font-size: 1.6rem;
          font-weight: 700;
          color: #3b82f6;
          margin: 0 0 1rem 0;
          display: inline-flex;
          align-items: center;
          gap: 1rem;
          padding: 0 3rem;
          line-height: 1.4;
        }

        .quote-icon-start-side,
        .quote-icon-end-side {
          color: rgba(59, 130, 246, 0.3);
          font-size: 1.3rem;
          animation: quoteFloatSide 3s ease-in-out infinite;
        }

        .quote-icon-start-side {
          animation-delay: 0s;
        }

        .quote-icon-end-side {
          animation-delay: 0.5s;
        }

        .title-underline-side {
          width: 100px;
          height: 4px;
          background: linear-gradient(90deg, #3b82f6, #8b5cf6);
          margin: 0 auto;
          border-radius: 2px;
          animation: widthGrowSide 1s ease-out;
        }

        /* Message Text */
        .message-text-side {
          color: #475569;
          line-height: 1.8;
          font-size: 1.1rem;
        }

        .paragraphs-container-side {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .paragraph-wrapper-side {
          overflow: hidden;
        }

        .paragraph-item-side {
          text-align: justify;
          margin: 0;
          padding: 1.5rem;
          background: #f8fafc;
          border-radius: 12px;
          border-left: 5px solid #3b82f6;
          opacity: 0;
          transform: translateX(-20px);
          animation: paragraphSlideInSide 0.6s ease-out forwards;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
          font-size: 1.05rem;
        }

        .paragraph-item-side:hover {
          transform: translateX(0) scale(1.01);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
          background: #f1f5f9;
          border-left-color: #2563eb;
        }

        .single-paragraph-side {
          position: relative;
          padding: 2rem;
          background: #f8fafc;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
        }

        .full-message-side {
          text-align: justify;
          margin: 0;
          line-height: 1.8;
          position: relative;
          padding: 0 2rem;
          font-size: 1.05rem;
        }

        .quote-icon-inline-start-side,
        .quote-icon-inline-end-side {
          color: rgba(59, 130, 246, 0.3);
          font-size: 1.4rem;
          vertical-align: super;
          position: absolute;
          animation: quoteFloatSide 3s ease-in-out infinite;
        }

        .quote-icon-inline-start-side {
          left: 0;
          top: 0;
          animation-delay: 0s;
        }

        .quote-icon-inline-end-side {
          right: 0;
          bottom: 0;
          animation-delay: 0.5s;
        }

        /* Closing Quote */
        .closing-quote-side {
          margin: 2rem 0;
          opacity: 0;
          animation: fadeInSide 0.6s ease-out 0.6s forwards;
        }

        .closing-quote-wrapper-side {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          background: linear-gradient(90deg, rgba(59, 130, 246, 0.05), transparent);
          padding: 1.8rem;
          border-radius: 16px;
          border-left: 5px solid #3b82f6;
        }

        .closing-icon-side {
          font-size: 2.2rem;
          color: #3b82f6;
          flex-shrink: 0;
        }

        .closing-quote-text {
          margin: 0;
          font-style: italic;
          color: #475569;
          font-size: 1.2rem;
          line-height: 1.6;
        }

        /* Signature Section */
        .signature-section-side {
          margin-top: 2.5rem;
          text-align: right;
        }

        .signature-line-side {
          width: 250px;
          height: 2px;
          background: linear-gradient(90deg, transparent, #cbd5e1);
          margin-left: auto;
          margin-bottom: 1.2rem;
        }

        .signature-content-side {
          display: inline-flex;
          flex-direction: column;
          align-items: flex-end;
        }

        .signature-name-side {
          font-size: 1.4rem;
          font-weight: 800;
          color: #1e293b;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .signature-designation-side {
          font-size: 1.1rem;
          color: #64748b;
          font-style: italic;
          margin-top: 0.3rem;
        }

        /* Message Actions */
        .message-actions-side {
          display: flex;
          gap: 1rem;
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px dashed #e2e8f0;
        }

        .action-btn {
          display: flex;
          align-items: center;
          padding: 0.8rem 1.5rem;
          background: white;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          color: #475569;
          font-weight: 600;
          font-size: 0.95rem;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .share-btn-side:hover {
          border-color: #3b82f6;
          color: #3b82f6;
        }

        .save-btn-side:hover {
          border-color: #f59e0b;
          color: #f59e0b;
        }

        /* Empty State */
        .empty-state-container {
          text-align: center;
          padding: 6rem 2rem;
          background: white;
          border-radius: 20px;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
          animation: fadeIn 0.8s ease-out;
          border: 1px solid #e2e8f0;
        }

        .empty-state-animation {
          margin-bottom: 2rem;
        }

        .empty-state-icon {
          width: 120px;
          height: 120px;
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(30, 64, 175, 0.05));
          border-radius: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #94a3b8;
          font-size: 3.5rem;
          animation: float 3s ease-in-out infinite;
        }

        .empty-state-title {
          font-size: 2rem;
          color: #1e293b;
          margin-bottom: 1rem;
          font-weight: 700;
        }

        .empty-state-description {
          font-size: 1.1rem;
          color: #64748b;
          max-width: 500px;
          margin: 0 auto;
        }

        /* Loading State */
        .fullpage-loader {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .loader-container {
          text-align: center;
        }

        .loader-spinner {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .spinner-circle {
          width: 18px;
          height: 18px;
          background: #3b82f6;
          border-radius: 50%;
          animation: bounce 1.4s infinite ease-in-out both;
        }

        .spinner-circle:nth-child(1) {
          animation-delay: -0.32s;
        }

        .spinner-circle:nth-child(2) {
          animation-delay: -0.16s;
        }

        .loader-text {
          font-size: 1.8rem;
          color: #1e293b;
          margin-bottom: 0.5rem;
          animation: fadeIn 1s ease-out;
        }

        .loader-subtext {
          color: #64748b;
          font-size: 1rem;
          animation: fadeIn 1s ease-out 0.5s both;
        }

        /* Error State */
        .fullpage-error {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .error-container {
          text-align: center;
          padding: 2.5rem;
          max-width: 500px;
          background: white;
          border-radius: 20px;
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
        }

        .error-icon {
          font-size: 3.5rem;
          color: #ef4444;
          margin-bottom: 1.2rem;
          animation: shake 0.5s ease-in-out, float 3s ease-in-out infinite 0.5s;
        }

        .error-title {
          font-size: 1.8rem;
          color: #dc2626;
          margin-bottom: 0.8rem;
          animation: fadeInUp 0.8s ease-out;
        }

        .error-description {
          color: #64748b;
          margin-bottom: 1.5rem;
          line-height: 1.6;
          animation: fadeInUp 0.8s ease-out 0.2s both;
        }

        .error-retry-btn {
          padding: 0.7rem 1.8rem;
          border-radius: 50px;
          font-weight: 600;
          background: linear-gradient(135deg, #3b82f6, #1d4ed8);
          border: none;
          transition: all 0.3s ease;
          color: white;
          animation: fadeInUp 0.8s ease-out 0.3s both;
        }

        .error-retry-btn:hover {
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
        }

        /* New Animations */
        @keyframes fadeInDown {
          0% {
            opacity: 0;
            transform: translateY(-30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        @keyframes pulseDot {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.3);
            opacity: 0.8;
          }
        }

        @keyframes cardSlideIn {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideIn {
          0% {
            width: 0;
            opacity: 0;
          }
          100% {
            width: 40px;
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        @keyframes fadeInSide {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }

        @keyframes paragraphSlideInSide {
          0% {
            opacity: 0;
            transform: translateX(-20px);
          }
          100% {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes quoteFloatSide {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0.3;
          }
          50% {
            transform: translateY(-5px) scale(1.1);
            opacity: 0.6;
          }
        }

        @keyframes widthGrowSide {
          0% {
            width: 0;
          }
          100% {
            width: 100px;
          }
        }

        @keyframes pulseIndicatorLarge {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
          }
          50% {
            transform: scale(1.2);
            box-shadow: 0 6px 16px rgba(16, 185, 129, 0.6);
          }
        }

        @keyframes starPulseLarge {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
          }
          50% {
            transform: scale(1.2);
            box-shadow: 0 6px 16px rgba(245, 158, 11, 0.6);
          }
        }

        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .main-container {
            padding: 0 1rem;
            max-width: 100%;
          }
          
          .leader-content-wrapper {
            grid-template-columns: 300px 1fr;
            gap: 2.5rem;
          }
          
          .leader-photo-wrapper {
            height: 350px;
          }
        }

        @media (max-width: 992px) {
          .portal-content {
            padding: 1.5rem 0;
          }
          
          .main-title {
            font-size: 2.8rem;
          }
          
          .title-icon {
            font-size: 3.5rem;
          }
          
          .leader-content-wrapper {
            grid-template-columns: 280px 1fr;
            gap: 2rem;
          }
          
          .leader-photo-wrapper {
            height: 320px;
          }
          
          .leader-message-card .card-body {
            padding: 2.5rem;
          }
          
          .leader-name-side {
            font-size: 1.6rem;
          }
          
          .message-title-text {
            font-size: 1.4rem;
            padding: 0 2rem;
          }
          
          .paragraph-item-side {
            padding: 1.2rem;
          }
        }

        @media (max-width: 768px) {
          .main-container {
            padding: 0 1rem;
          }
          
          .main-title {
            font-size: 2.4rem;
          }
          
          .title-icon {
            font-size: 3rem;
          }
          
          .title-subtext {
            font-size: 1.1rem;
          }
          
          .tabs-container {
            padding: 0.8rem;
          }
          
          .tab-button {
            padding: 0.8rem 1.5rem;
            font-size: 0.9rem;
          }
          
          .leader-message-card .card-body {
            padding: 2rem;
          }
          
          .leader-content-wrapper {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
          
          .leader-photo-container {
            position: static;
          }
          
          .leader-photo-wrapper {
            height: 400px;
            max-width: 400px;
            margin: 0 auto;
          }
          
          .leader-basic-info-side {
            max-width: 400px;
            margin: 0 auto;
          }
          
          .message-title-text {
            padding: 0 1rem;
            font-size: 1.3rem;
          }
          
          .signature-section-side {
            text-align: center;
          }
          
          .signature-line-side {
            margin: 0 auto 1.2rem auto;
          }
          
          .signature-content-side {
            align-items: center;
          }
          
          .message-actions-side {
            justify-content: center;
          }
        }

        @media (max-width: 576px) {
          .portal-content {
            padding: 1rem 0;
          }
          
          .main-container {
            padding: 0 0.5rem;
          }
          
          .main-title {
            font-size: 2rem;
          }
          
          .title-icon {
            font-size: 2.5rem;
          }
          
          .title-subtext {
            font-size: 1rem;
          }
          
          .tabs-wrapper {
            justify-content: flex-start;
          }
          
          .tab-button {
            padding: 0.7rem 1.2rem;
            font-size: 0.85rem;
          }
          
          .tab-badge {
            display: none;
          }
          
          .leader-message-card .card-body {
            padding: 1.5rem;
          }
          
          .leader-photo-wrapper {
            height: 320px;
          }
          
          .photo-icon-large {
            font-size: 4rem;
          }
          
          .leader-name-side {
            font-size: 1.4rem;
          }
          
          .role-badge-side {
            font-size: 0.9rem;
            padding: 0.7rem 1.2rem;
          }
          
          .message-title-text {
            font-size: 1.2rem;
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .quote-icon-start-side,
          .quote-icon-end-side {
            position: static;
            display: block;
          }
          
          .paragraph-item-side {
            padding: 1rem;
            font-size: 1rem;
          }
          
          .closing-quote-wrapper-side {
            flex-direction: column;
            text-align: center;
            gap: 1rem;
            padding: 1.5rem;
          }
          
          .empty-state-container {
            padding: 3rem 1rem;
          }
          
          .empty-state-icon {
            width: 100px;
            height: 100px;
            font-size: 2.5rem;
          }
          
          .empty-state-title {
            font-size: 1.6rem;
          }
        }

        @media (max-width: 400px) {
          .main-title {
            font-size: 1.8rem;
          }
          
          .title-icon {
            font-size: 2.2rem;
          }
          
          .tab-button {
            padding: 0.6rem 1rem;
            font-size: 0.8rem;
          }
          
          .leader-photo-wrapper {
            height: 280px;
          }
          
          .photo-icon-large {
            font-size: 3.5rem;
          }
          
          .leader-message-card .card-body {
            padding: 1.25rem;
          }
          
          .leader-name-side {
            font-size: 1.3rem;
          }
          
          .role-badge-side {
            font-size: 0.85rem;
            padding: 0.6rem 1rem;
          }
          
          .message-actions-side {
            flex-direction: column;
          }
          
          .action-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default LeadershipMessages;